import { Column, Row } from 'app/design/primitives/box';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';

import { useNavigation } from 'app/hooks/navigation';
import { getI18nLanguage } from 'app/lib/language';
import { safelyOpenURL } from 'app/lib/navigation';
import { preparePolicyUrl } from 'app/lib/text';
import { useUser } from 'app/network/user';
import type { SpecificRouteProp } from 'app/types/navigation';

import { Trans, useTranslation } from 'react-i18next';
import { Pressable, View } from 'react-native';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

const EMOJIS = ['👋', '🗓️', '🫳', '✨'];
const EMOJIS_LENDER = ['👋', '🗓️', '🫴', '✨'];

export const HowItWorksScreen = function HowItWorksScreen({
  route,
}: {
  route: SpecificRouteProp<'chat-how-it-works'>;
}) {
  const isLender = route.params.isLender;
  const navigation = useNavigation();
  const { t } = useTranslation(['one-channel', 'contact-us']);
  const { data: user } = useUser();
  const i18nLanguage = getI18nLanguage();

  const prefix = isLender ? 'howItWorks.lend' : 'howItWorks.borrow';
  const emojis = isLender ? EMOJIS_LENDER : EMOJIS;

  const visitHelpCenter = () => {
    void safelyOpenURL(preparePolicyUrl(t('faqLink', { ns: 'contact-us' }), i18nLanguage, user));
  };

  return (
    <Page>
      <Row alignItems="center" gap="spacing.general.x2" style={{ marginBottom: theme.spacing['spacing.general.x4'] }}>
        <Pressable
          accessibilityLabel={t('close', { ns: 'accessibility' })}
          accessibilityRole="button"
          hitSlop={12}
          onPress={() => navigation.goBack()}
        >
          <IconX strokeWidth={ICON_STROKE_WIDTH} size={22} color={theme.colors['text.primary']} />
        </Pressable>
        <Text flex={1} textAlign="center" fontWeight="600" fontSize={20} color="text.primary">
          {t(isLender ? 'howItWorks.screenTitle.lend' : 'howItWorks.screenTitle.borrow')}
        </Text>
        <View style={{ width: 22 }} />
      </Row>

      <Column gap="spacing.general.x8">
        <Text fontWeight="700" fontSize={20}>
          {t('howItWorks.title')}
        </Text>

        <Column gap="spacing.general.x8">
          {[1, 2, 3, 4].map((step) => (
            <Row key={step} alignItems="center" gap="spacing.general.x4">
              <View
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 32,
                  backgroundColor: theme.colors['surface.brand.secondary-5.subtle'],
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Text fontSize={30}>{emojis[step - 1]}</Text>
              </View>
              <Column flex={1} minWidth={0}>
                <Text fontSize={16} variant="bodyBase">
                  <Trans
                    components={{ bold: <Text fontWeight="700" /> }}
                    i18nKey={`${prefix}.step${String(step)}`}
                    ns="one-channel"
                  />
                </Text>
              </Column>
            </Row>
          ))}
        </Column>

        <Column alignItems="center" gap="spacing.general.x2" marginTop="spacing.general.x8">
          <Text color="text.primary" textAlign="center" variant="bodyBase">
            {t('howItWorks.assistanceQuestion')}
          </Text>
          <Pressable onPress={visitHelpCenter}>
            <Text color="text.emphasized" fontWeight="700" fontSize={16}>
              {t('howItWorks.visitHelpCenter')} →
            </Text>
          </Pressable>
        </Column>
      </Column>
    </Page>
  );
};

export const HowItWorksInlineCard = function HowItWorksInlineCard({ isLender }: { isLender: boolean }) {
  const { t } = useTranslation('one-channel');
  const prefix = isLender ? 'howItWorks.lend' : 'howItWorks.borrow';
  const emojis = isLender ? EMOJIS_LENDER : EMOJIS;

  return (
    <Column p="spacing.general.x2">
      <Column
        p="spacing.general.x4"
        backgroundColor="surface.default"
        borderRadius="radius.lg"
        borderWidth={1}
        borderColor="surface.disabled"
      >
        <Text fontWeight="700" fontSize={18} marginBottom="spacing.general.x4">
          {t('howItWorks.title')}
        </Text>

        <Column gap="spacing.general.x4">
          {[1, 2, 3, 4].map((step) => (
            <Row key={step} alignItems="flex-start" gap="spacing.general.x3">
              <Column
                alignItems="center"
                justifyContent="center"
                width={44}
                height={44}
                borderRadius="radius.full"
                backgroundColor="surface.brand.secondary-5.subtle"
              >
                <Text fontSize={22}>{emojis[step - 1]}</Text>
              </Column>
              <Column flex={1} minWidth={0}>
                <Text fontSize={14} lineHeight={20} variant="bodyBase">
                  <Trans
                    components={{ bold: <Text fontWeight="700" /> }}
                    i18nKey={`${prefix}.step${String(step)}`}
                    ns="one-channel"
                  />
                </Text>
              </Column>
            </Row>
          ))}
        </Column>
      </Column>
    </Column>
  );
};
